import { API_BASE_URL } from "@/constants/api";
import type { DirectCategoryCount } from "@/lib/categories/counts";
import { categoriesApi } from "@/services/dashboard-api.client";
import type {
  CategoryDescriptionTemplateConfiguration,
  CategoryTitleTemplateConfiguration,
  ResolvedCategoryDescriptionTemplate,
  ResolvedCategoryTitleTemplate,
} from "@sakura-tech-lab/dashboard-api";

export type LanguageMap = Record<string, string>;
export type OptionalLanguageMap = Partial<LanguageMap>;
export type AttributeValue = string | number | boolean;

export interface AttributeChoice {
  value: AttributeValue;
  label: LanguageMap;
  slug?: OptionalLanguageMap;
}

export interface AttributeDefinition {
  type: "string" | "number" | "integer" | "boolean";
  description?: string;
  label: LanguageMap;
  slug?: OptionalLanguageMap;
  choices?: AttributeChoice[];
}

export type AttributeSchema = Record<string, AttributeDefinition>;

export interface CategoryTreeNode {
  id: string;
  name: LanguageMap;
  slug: string | null;
  category_slug: OptionalLanguageMap | null;
  parentId: string | null;
  isCanonical: boolean;
  description: string | null;
  children: CategoryTreeNode[];
  reprocessing?: { queued?: number };
}

export type Category = Omit<CategoryTreeNode, "children"> & {
  children?: CategoryTreeNode[];
};

export type ConfigurationMode = "inherit" | "override" | "empty";

export interface CategoryAttributeConfiguration {
  id?: string;
  category_id?: string | null;
  mode: ConfigurationMode;
  attributes: AttributeSchema;
  reprocessing?: { queued?: number };
}

export interface ResolvedAttributes {
  categoryId: string | null;
  own: CategoryAttributeConfiguration | null;
  resolved: {
    attributes: AttributeSchema;
    inheritedFrom: LanguageMap | "general" | null;
  };
}

export type CategoryTitleTemplateRow = CategoryTitleTemplateConfiguration;
export type TitleTemplateInfo = ResolvedCategoryTitleTemplate;
export type CategoryDescriptionTemplateRow = CategoryDescriptionTemplateConfiguration;
export type DescriptionTemplateInfo = ResolvedCategoryDescriptionTemplate;

export interface TitlePlaceholders {
  placeholders: string[];
  systemDefault?: string;
}

type JsonRecord = Record<string, unknown>;

async function categoryFetch(
  catalogueId: string,
  path = "",
  init: RequestInit = {},
): Promise<any> {
  const url = new URL(`${API_BASE_URL}/api/v1/category${path}`, window.location.origin);
  url.searchParams.set("catalogueId", catalogueId);
  const response = await fetch(url.toString(), {
    ...init,
    credentials: "include",
    headers: {
      ...(init.body === undefined ? {} : { "Content-Type": "application/json" }),
      ...init.headers,
    },
  });
  const payload: unknown = await response.json().catch(() => null);
  if (
    !response.ok ||
    payload === null ||
    typeof payload !== "object" ||
    Array.isArray(payload)
  ) {
    const error = payload as JsonRecord | null;
    throw new Error(
      typeof error?.error === "string"
        ? error.error
        : `Category request failed (${response.status})`,
    );
  }
  return (payload as JsonRecord).data;
}

export const CategoryService = {
  getTree: (catalogueId: string): Promise<CategoryTreeNode[]> =>
    categoryFetch(catalogueId),

  getProductCounts: (catalogueId: string): Promise<DirectCategoryCount[]> =>
    categoryFetch(catalogueId, "/counts"),

  createCategory: (
    catalogueId: string,
    input: {
      name: LanguageMap;
      category_slug?: OptionalLanguageMap | null;
      slug: string;
      description?: string | null;
      parentId?: string | null;
      isCanonical?: boolean;
    },
  ): Promise<Category> =>
    categoryFetch(catalogueId, "", { method: "POST", body: JSON.stringify(input) }),

  updateCategory: (
    catalogueId: string,
    id: string,
    input: {
      name?: LanguageMap;
      category_slug?: OptionalLanguageMap | null;
      description?: string | null;
      parentId?: string | null;
      isCanonical?: boolean;
    },
  ): Promise<Category> =>
    categoryFetch(catalogueId, `/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),

  deleteCategory: (catalogueId: string, id: string) =>
    categoryFetch(catalogueId, `/${encodeURIComponent(id)}`, { method: "DELETE" }),

  getAttributes: (catalogueId: string, id: string): Promise<ResolvedAttributes> =>
    categoryFetch(catalogueId, `/${encodeURIComponent(id)}/attributes`),

  saveAttributes: (
    catalogueId: string,
    id: string,
    payload: {
      attributes: AttributeSchema;
      mode?: ConfigurationMode;
    },
  ): Promise<CategoryAttributeConfiguration> =>
    categoryFetch(catalogueId, `/${encodeURIComponent(id)}/attributes`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),

  getTitleTemplate: async (
    catalogueId: string,
    id: string,
  ): Promise<TitleTemplateInfo> =>
    (await categoriesApi.getCategoryTitleTemplate({ catalogueId, id })).data,

  setTitleTemplate: (
    catalogueId: string,
    id: string,
    titleTemplate: string | null,
  ): Promise<CategoryTitleTemplateRow> =>
    categoriesApi.updateCategoryTitleTemplate({
      catalogueId,
      id,
      requestBody: { titleTemplate },
    }).then((response) => response.data),

  getTitlePlaceholders: async (catalogueId: string): Promise<TitlePlaceholders> =>
    (await categoriesApi.listTitleTemplatePlaceholders({ catalogueId })).data,

  getDescriptionTemplate: (
    catalogueId: string,
    id: string,
  ): Promise<DescriptionTemplateInfo> =>
    categoriesApi.getCategoryDescriptionTemplate({ catalogueId, id })
      .then((response) => response.data),

  setDescriptionTemplate: (
    catalogueId: string,
    id: string,
    input: {
      descriptionTemplate: string | null;
      descriptionMode: "replace" | "extend";
      descriptionTargetWordCount: number | null;
    },
  ): Promise<CategoryDescriptionTemplateRow> =>
    categoriesApi.updateCategoryDescriptionTemplate({
      catalogueId,
      id,
      categoryDescriptionTemplateUpdateRequest: input,
    }).then((response) => response.data),
};
